/**
 * SoleadLab 2.0 - Project API Service
 *
 * Wrapper around the sites/base-data API endpoints for project CRUD.
 */

import { apiFetch, apiFetchRaw, ApiError } from './apiFetch';

/** Returned by saveProject on success. */
export interface SaveResult {
  updated_at: string;
  version: number;
}

/** Returned by saveProject on 409 version conflict. */
export interface SaveConflict {
  conflict: true;
  serverVersion: number;
}

export interface SiteListItem {
  id: string;
  client_id: string;
  // Tenant (organization) the site belongs to; null only on legacy rows.
  org_id?: string | null;
  name: string;
  location?: string;
  capacity_kw?: number;
  created_at: string;
  updated_at: string;
  client_name?: string;
  // Newest scan day (`YYYY-MM-DD`) across the site's photo indications and
  // orthophotos — what the dashboard shows as "Last capture" (NOT updated_at,
  // which only moves on metadata edits). Not part of the list response: the
  // dashboard merges it from listSiteCaptureDates(). undefined = not fetched
  // yet, null = nothing dated exists.
  last_capture_date?: string | null;
  // Dashboard map (added with migration 0036). Sites pre-dating that migration
  // and never re-saved still return NULL for all three — the dashboard treats
  // those as "no pin yet" and shows a "Set location" CTA in the sidebar.
  latitude?: number | null;
  longitude?: number | null;
  location_source?: 'manual' | 'map_view' | 'orthophoto' | 'photo_exif' | 'srt' | 'datapoint' | null;
  // M-05C: archive (soft-delete) fields, present on rows returned with
  // ?includeArchived=true (platform-admin only). `deleted_by` / `delete_reason`
  // are intentionally NOT declared here — M-05C must not display them.
  deleted_at?: string | null;
  status?: string | null;
}

export interface SetSiteLocationResult {
  latitude: number;
  longitude: number;
  location_source: 'manual';
}

export interface SiteListResponse {
  items: SiteListItem[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface ClientItem {
  id: string;
  name: string;
}

/**
 * List sites (projects) with pagination
 */
export async function listProjects(
  page = 1,
  limit = 50,
  search?: string,
  options?: { includeArchived?: boolean }
): Promise<SiteListResponse> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) params.set('search', search);
  // M-05C: include archived (soft-deleted) sites. The backend honors this only
  // for platform-admin callers and ignores it otherwise, so it is safe to send.
  if (options?.includeArchived) params.set('includeArchived', 'true');
  return apiFetch<SiteListResponse>(`/api/v2/sites?${params}`);
}

/**
 * Newest scan day (`YYYY-MM-DD`) per visible site, keyed by site id. Separate
 * from the list because the server-side lookup is slow (scans every
 * indication row); the dashboard fetches it in parallel and merges it in.
 */
export async function listSiteCaptureDates(): Promise<Record<string, string>> {
  const res = await apiFetch<{ dates: Record<string, string> }>('/api/v2/sites/capture-dates');
  return res.dates ?? {};
}

/**
 * Set a manual pin on the dashboard map. Backend records
 * `location_source='manual'` so subsequent saves / asset imports won't
 * overwrite the coordinate. Requires admin or operator role.
 */
export async function setSiteLocation(
  siteId: string,
  latitude: number,
  longitude: number
): Promise<SetSiteLocationResult> {
  return apiFetch<SetSiteLocationResult>(`/api/v2/sites/${siteId}/location`, {
    method: 'PUT',
    body: JSON.stringify({ latitude, longitude }),
  });
}

export interface DeriveCoordinatesResult {
  updated: number;
  attempted: number;
  total: number;
}

/**
 * Backfill site coordinates for the caller's tenant. Idempotent — skips
 * sites that already have coords or were manually pinned. Safe to call
 * silently on dashboard mount; cheap once everything is populated.
 */
export async function deriveSiteCoordinates(): Promise<DeriveCoordinatesResult> {
  return apiFetch<DeriveCoordinatesResult>('/api/v2/sites/derive-coordinates', {
    method: 'POST',
  });
}

/**
 * Fetch project data (base data) for a site.
 * Reads the ETag header for optimistic-concurrency version tracking.
 */
export async function fetchProject(
  siteId: string
): Promise<{ data: Record<string, unknown>; updated_at: string | null; version: number }> {
  const res = await apiFetchRaw(`/api/v2/sites/${siteId}/base`);

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } }
      | null;
    throw new Error(body?.error?.message || `Failed to fetch project (${res.status})`);
  }

  const etag = res.headers.get('ETag');
  const etagVersion = etag ? parseInt(etag, 10) : NaN;

  const body = (await res.json()) as {
    data: { data: Record<string, unknown>; updated_at: string | null; version?: number };
  };
  // Prefer ETag header; fall back to version in response body.
  const resolvedVersion = !isNaN(etagVersion) ? etagVersion : (body.data.version ?? 0);
  return { ...body.data, version: resolvedVersion };
}

/**
 * Save project data (base data) for a site.
 * Sends If-Match header with the known version for optimistic concurrency.
 * On 409 conflict, returns SaveConflict instead of throwing.
 *
 * The backend requires If-Match unconditionally — for sites that have never
 * been saved, the current version is 0, so we send `If-Match: 0` rather than
 * omitting the header.
 */
export async function saveProject(
  siteId: string,
  data: Record<string, unknown>,
  version?: number
): Promise<SaveResult | SaveConflict> {
  const headers: Record<string, string> = {
    'If-Match': String(version ?? 0),
  };

  const res = await apiFetchRaw(`/api/v2/sites/${siteId}/base`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });

  // 409 = version conflict — return conflict signal, don't throw.
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { details?: { currentVersion?: number } } }
      | null;
    const serverVersion = body?.error?.details?.currentVersion ?? 0;
    return { conflict: true, serverVersion };
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { message?: string } }
      | null;
    throw new Error(body?.error?.message || `Failed to save project (${res.status})`);
  }

  const body = (await res.json()) as { data: { updated_at: string; version?: number } };

  // Parse new version from ETag header; fall back to version in response body
  // (Cloudflare edge may strip ETag headers from Worker responses).
  const etag = res.headers.get('ETag');
  const etagVersion = etag ? parseInt(etag, 10) : NaN;
  const newVersion = !isNaN(etagVersion) ? etagVersion : (body.data.version ?? 0);

  return { updated_at: body.data.updated_at, version: newVersion };
}

/**
 * Create a new site (project). `clientId` is the legacy clients-table FK;
 * SaaS-native users (no client_id) should omit it — the backend autovivifies
 * a shadow client per org. Passing an empty string trips the schema's
 * `min(1)` check and 422s, so callers should pass `undefined` (or a non-
 * empty id) rather than `''`.
 */
export async function createProject(
  name: string,
  clientId?: string
): Promise<SiteListItem> {
  const body: { name: string; clientId?: string } = { name };
  if (clientId) body.clientId = clientId;
  return apiFetch<SiteListItem>('/api/v2/sites', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Fields a site row can be patched with via PUT /api/v2/sites/:id.
 *  Omitted = unchanged; null clears location/capacity. */
export interface UpdateProjectInput {
  name?: string;
  location?: string | null;
  capacityKw?: number | null;
}

/**
 * Patch site metadata (name / location / capacity). Allowed for platform
 * admin and org owner/admin of the site's tenant. The backend returns the
 * bare site row (no `client_name` join), so callers should merge the result
 * into their list item rather than replace it.
 */
export async function updateProject(
  siteId: string,
  patch: UpdateProjectInput
): Promise<SiteListItem> {
  return apiFetch<SiteListItem>(`/api/v2/sites/${siteId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

/**
 * Delete a site (project) and all its data
 */
export async function deleteProject(siteId: string): Promise<void> {
  await apiFetch<void>(`/api/v2/sites/${siteId}`, { method: 'DELETE' });
}

/** Result of restoring an archived site (M-05C). */
export interface RestoreResult {
  site_id: string;
  archived: boolean;
  /** false when the site was already active (idempotent no-op). */
  restored: boolean;
}

/**
 * Restore an archived (soft-deleted) site. Platform-admin only — the backend
 * 403s otherwise. Idempotent: an already-active site returns restored:false.
 * Restore does NOT re-enable share links, upload links, or SCADA credentials.
 */
export async function restoreProject(siteId: string): Promise<RestoreResult> {
  return apiFetch<RestoreResult>(`/api/v2/sites/${siteId}/restore`, {
    method: 'POST',
  });
}

/** Purge job lifecycle status (M-05D). */
export type PurgeStatus =
  | 'previewed' | 'started' | 'r2_deleting' | 'd1_deleting' | 'completed' | 'partial' | 'failed';

/** Safe purge-preview counts (M-05D-1). Counts/categories only — never raw keys. */
export interface PurgePreviewResult {
  site_id: string;
  eligible: boolean;
  archived_at: string | null;
  counts: {
    d1: Record<string, number | null>;
    r2: Record<string, number | null>;
  };
  notes?: Record<string, unknown>;
}

/** Safe purge result (M-05D-2). Counts/status only — never raw keys. */
export interface PurgeResult {
  site_id: string;
  operation_id: string;
  status: PurgeStatus;
  r2_deleted_count?: number;
  r2_failed_count?: number;
  retryable?: boolean;
  counts?: { d1?: Record<string, number> };
}

/** Safe purge job-state read (M-05D-2). */
export interface PurgeStatusResult {
  operation_id: string;
  site_id: string;
  status: PurgeStatus;
  reason_code: string | null;
  reason_present: number;
  r2_deleted_count: number;
  r2_failed_count: number;
  counts: { r2: unknown; d1: unknown };
  last_error_code: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

/**
 * Read-only physical-purge preview (dry-run). Platform-admin + archived-site
 * only (the backend 403s / 409s otherwise). Returns safe aggregate counts only.
 */
export async function purgePreviewProject(siteId: string): Promise<PurgePreviewResult> {
  return apiFetch<PurgePreviewResult>(`/api/v2/sites/${siteId}/purge-preview`);
}

/**
 * Start (or resume) a physical purge / true erasure of an archived site.
 * Platform-admin only; requires the exact `confirmName` + an enum `reasonCode`.
 * IRREVERSIBLE. Returns safe job status/counts (completed/partial/failed/…).
 */
export async function purgeProject(
  siteId: string,
  opts: { confirmName: string; reasonCode: string }
): Promise<PurgeResult> {
  return apiFetch<PurgeResult>(`/api/v2/sites/${siteId}/purge`, {
    method: 'POST',
    body: JSON.stringify({ confirmName: opts.confirmName, reasonCode: opts.reasonCode }),
  });
}

/** Read the latest purge job state for a site (safe status/counts only). */
export async function getPurgeStatus(siteId: string): Promise<PurgeStatusResult> {
  return apiFetch<PurgeStatusResult>(`/api/v2/sites/${siteId}/purge-status`);
}

/**
 * List clients (for project creation dropdown)
 */
/** Organization row as returned by GET /api/v2/admin/organizations (platform admin). */
export interface AdminOrganizationItem {
  id: string;
  name: string;
  plan_id: string | null;
  status: string | null;
  site_count: number;
}

/** Organizations a platform admin can move a site to. */
export async function listAdminOrganizations(): Promise<AdminOrganizationItem[]> {
  const res = await apiFetch<AdminOrganizationItem[]>('/api/v2/admin/organizations?limit=200');
  return Array.isArray(res) ? res : [];
}

export interface MoveProjectResult {
  site_id: string;
  moved: boolean;
  from_org_id?: string | null;
  to_org_id?: string;
  client_id: string;
  share_links_repointed_count?: number;
  dock_missions_repointed_count?: number;
}

/**
 * Platform-admin: move a site to another organization
 * (POST /api/v2/sites/:id/move). `confirmName` = the exact current name.
 */
export async function moveProject(
  siteId: string,
  input: { targetOrgId: string; confirmName: string }
): Promise<MoveProjectResult> {
  return apiFetch<MoveProjectResult>(`/api/v2/sites/${siteId}/move`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listClients(): Promise<ClientItem[]> {
  // API returns either flat array or { items: [...] }; both shapes appear
  // depending on whether the endpoint paginates.
  const data = await apiFetch<ClientItem[] | { items: ClientItem[] }>(
    '/api/v2/clients'
  ).catch((err: ApiError) => {
    // 403 here is non-fatal — non-admin users just don't see the client list.
    if (err.status === 403) return [] as ClientItem[];
    throw err;
  });
  return Array.isArray(data) ? data : (data?.items || []);
}
